import Component from '../../components/component/component';
import { debounce, DebouncedFunction } from '../../components/helpers/helpers';
import { tabsSettings } from '../../components/tabs/tabs';
import AjaxPage from '../ajax/ajax-page';

import ITooltipsterOptions = JQueryTooltipster.ITooltipsterOptions;

import './pricelist.scss';

interface IPricelistSettings {
    rowSelector: string;
    tableSelector: string;
    tabsCurrentClass: string;
    tabsLinkSelector: string;
    tabsListSelector: string;
    tabsSelector: string;
    additionalHoverBaseClass: string;
    additionalHoverStateClass: string;
}

export type TPricelistActiveComponent = 'floorplan-tabs' | 'long-house' | 'sequence';

export default class Pricelist extends Component {
    static initSelector: string = '.js-pricelist';

    activeComponent: TPricelistActiveComponent;
    currentId: string | null;
    rows: JQuery;
    settings: IPricelistSettings;
    table: JQuery;
    tabs: JQuery;
    tabsLinks: JQuery;
    tabsList: JQuery;
    tooltipItems: JQuery;
    debouncedMouseHandler: DebouncedFunction<() => void>;
    debouncedTooltipHandler: DebouncedFunction<() => void>;

    constructor(target: HTMLElement) {
        super(target);

        this.settings = {
            rowSelector: '.table__row',
            tableSelector: '.pricelist__table',
            tabsCurrentClass: tabsSettings.currentClass,
            tabsLinkSelector: '.tabs__nav-link',
            tabsListSelector: '.tabs__nav-list',
            tabsSelector: '.pricelist__tabs',
            additionalHoverBaseClass: 'js-additional-hover--',
            additionalHoverStateClass: 'is-hovering',
        };

        this.currentId = null;
        this.table = this.element.find(this.settings.tableSelector);
        this.rows = this.table.find(this.settings.rowSelector);
        this.tabs = this.element.find(this.settings.tabsSelector);
        this.tabsList = this.tabs.find(this.settings.tabsListSelector).first();
        this.tabsLinks = this.tabsList.find(this.settings.tabsLinkSelector);

        this.init();
    }

    public static removeHighlights(): void {
        $('tr.is-highlighted, .sequence .is-highlighted, .floorplan-tabs .is-highlighted').removeClass('is-highlighted');
    }

    public static highlightApartment(id: string): void {
        const row: JQuery = $('tr[data-id="' + id + '"]');

        if (row.length) {
            row.addClass('is-highlighted');
            row.parent().scrollTop(row.parent().scrollTop() + row.position().top);
        }
    }

    public static initTooltip(element: HTMLElement): void {
        const apartment: JQuery = $(element);
        const isSequenceElement: boolean = apartment.hasClass('sequence__apt');
        const apartmentId: string = (apartment.data('name') ? apartment.data('name') : apartment.data('id'));
        const tooltipData: IToolTipData = window.gotoAndPlay.tooltipData[apartmentId];

        if (tooltipData && !apartment.data('tooltip')) {
            const isAvailable: boolean = tooltipData.status === 'available';
            const theme: string = (isAvailable ? '' : 'tooltip--light');
            const content: JQuery = $(`<div class="tooltip__inner"><div class="tooltip__title">${tooltipData.number}</div><div class="tooltip__content">${isAvailable ? `${tooltipData.rooms}<br />${tooltipData.size}<br />${tooltipData.price}<br />` : tooltipData.price}</div></div>`);
            const tooltipsterData: ITooltipsterOptions = {
                animation: 'fade',
                animationDuration: 400,
                content,
                contentCloning: true,
                delay: 0,
                distance: -10,
                theme,
                trigger: 'custom',
                triggerClose: {
                    mouseleave: true,
                    originClick: true,
                    tap: true,
                },
                triggerOpen: {
                    mouseenter: true,
                    tap: true,
                },
            };

            if (!isAvailable) {
                apartment.addClass('is-sold tooltip--light');
            }

            apartment.on('click', (event: JQuery.ClickEvent): void => {
                event.preventDefault();
                if (AjaxPage.isAjaxEvent(event)) {
                    AjaxPage.changePage(tooltipData.link);
                } else {
                    document.location.href = tooltipData.link;
                }
            });
            apartment.removeAttr('id');
            apartment.data('tooltip', apartment.tooltipster(tooltipsterData));
            if (!isSequenceElement) {
                apartment.on('mouseover mouseout', (event: JQuery.TriggeredEvent): void => {
                    switch (event.type) {
                        case 'mouseover':
                            Pricelist.highlightApartment(apartmentId);
                            break;

                        case 'mouseout':
                            Pricelist.removeHighlights();
                            break;
                    }
                });
            }
        }
    }

    init(): void {
        this.rows.on('mouseenter', this.onRowEnter.bind(this));
        this.rows.on('mouseleave', this.onRowLeave.bind(this));
        this.debouncedMouseHandler = debounce(this.selectApartment.bind(this), 250);
        this.debouncedTooltipHandler = debounce(this.openTooltip.bind(this), 500);
        this.element.find('.sequence').on('sequence.complete', this.showSequenceApartment.bind(this));

        document.addEventListener('leaveApartmentModelOverlay', () => {
            Pricelist.removeHighlights();
        });

        document.addEventListener('enterApartmentModelOverlay', (event: CustomEvent) => {
            Pricelist.highlightApartment(event.detail.number);
        });
    }

    get activeContentType(): TPricelistActiveComponent {
        return this.tabsList.find('.' + this.settings.tabsCurrentClass).first().data('content-type').split('--')[0];
    }

    onRowEnter(event: JQuery.MouseEnterEvent): void {
        const target: JQuery = $(event.currentTarget);
        const additionalHover: string = target.data('hover');
        const roomNr: string = target.data('id');

        if (roomNr && !target.hasClass('is-disabled')) {
            const targetApartment: JQuery = $(`.houseplan__apartment[data-apartment="${roomNr}"]`);

            if (targetApartment) {
                targetApartment.addClass('is-active');
            }

            document.dispatchEvent(new CustomEvent('enterApartmentTableRow', {
                detail: {
                    number: roomNr,
                },
            }));
        }

        if (additionalHover) {
            $(`.${this.settings.additionalHoverBaseClass + additionalHover}`).trigger('mouseenter');
        }

        this.currentId = target.data('id');
        this.debouncedMouseHandler();
    }

    onRowLeave(event: JQuery.MouseLeaveEvent): void {
        const target: JQuery = $(event.currentTarget);
        const additionalHover: string = target.data('hover');
        const roomNr: string = target.data('id');

        if (roomNr && !target.hasClass('is-disabled')) {
            const targetApartment: JQuery = $(`.houseplan__apartment[data-apartment="${roomNr}"]`);

            if (targetApartment) {
                targetApartment.removeClass('is-active');
            }

            document.dispatchEvent(new CustomEvent('leaveApartmentTableRow', {
                detail: {
                    number: roomNr,
                },
            }));
        }

        if (additionalHover) {
            $(`.${this.settings.additionalHoverBaseClass + additionalHover}`).trigger('mouseleave');
        }

        this.currentId = null;
        this.debouncedMouseHandler();
    }

    selectApartment(): void {
        this.closeTooltips();
        if (this.currentId) {
            const currentApartment: IToolTipData = window.gotoAndPlay.tooltipData[this.currentId];

            if (currentApartment) {
                switch (this.activeContentType) {
                    case 'floorplan-tabs':
                        this.showFloorPlanApartment(currentApartment);
                        break;

                    case 'long-house':
                        // TODO
                        break;

                    case 'sequence':
                        this.showSequenceApartment();
                        break;
                }
            }
        }
    }

    closeTooltips(): void {
        this.element.find('.tooltipstered').tooltipster('close');
        Pricelist.removeHighlights();
    }

    openTooltip(): void {
        if (this.currentId) {
            this.closeTooltips();
            let tooltip: JQuery | undefined;

            switch (this.activeContentType) {
                case 'floorplan-tabs':
                    tooltip = this.element.find(`.is-open .floorplan-tabs__item-content--inner .floorplan__overlay[data-id="${this.currentId}"]:visible`);
                    if (!tooltip.length) {
                        tooltip = this.element.find(`.is-open .floorplan-tabs__item-content--inner .floorplan__overlay[data-name="${this.currentId}"]:visible`);
                    }
                    break;

                case 'sequence':
                    tooltip = this.element.find(`.is-open .is-sequence-active .sequence__apt[data-name="${this.currentId}"]:visible`);
                    break;
            }

            if (tooltip?.length) {
                tooltip.addClass('is-highlighted');
                tooltip.tooltipster('open');
            }
        }
    }

    showFloorPlanApartment(currentApartment: IToolTipData): void {
        const tabControl: JQuery = this.element.find(`.is-open .floorplan-tabs .js-tabs-control[data-floor="${currentApartment.floorNr}"]`);

        if (tabControl.hasClass('is-current')) {
            this.openTooltip();
        } else {
            tabControl.trigger('click');
            this.debouncedTooltipHandler();
        }
    }

    showSequenceApartment(): void {
        if (this.currentId) {
            let sequenceSlide: JQuery;

            const currentSequence: JQuery = this.element.find('.is-open .is-sequence-active');
            const sequenceAptWithCorrectCorner: JQuery = this.element.find(`.sequence__apt[data-name="${this.currentId}"][data-right-corner="true"]`);

            // Some apartments, which are situated at the corners of the building, can have an correct corner attribute. When it exists, prefer it to current slide's apartment.
            if (sequenceAptWithCorrectCorner.length) {
                sequenceSlide = sequenceAptWithCorrectCorner.parents('.sequence__slide');
            } else {
                sequenceSlide = currentSequence.find(`.sequence__apt[data-name="${this.currentId}"]`).parents('.sequence__slide');
            }


            if (sequenceSlide.hasClass('is-sequence-active')) {
                this.openTooltip();
            } else {
                const nextSlide: JQuery = currentSequence.next('.sequence__slide');
                const prevSlide: JQuery = currentSequence.prev('.sequence__slide');
                const aptSearchtAttr: string = `[data-name="${this.currentId}"]` + (sequenceAptWithCorrectCorner.length ? `[data-right-corner="true"]` : '');

                if (nextSlide.find(`.sequence__apt${aptSearchtAttr}`).length) {
                    this.element.find('.is-open .sequence__slider-nav .sequence__button--next').trigger('click');
                } else if (prevSlide.find(`.sequence__apt${aptSearchtAttr}`).length) {
                    this.element.find('.is-open .sequence__slider-nav .sequence__button--prev').trigger('click');
                } else {
                    this.element.find('.is-open .sequence__slider-nav .sequence__button--next').trigger('click');
                }
            }
        }
    }
}
